import { useEffect, useState } from "react";
import api from "./api";

/* Geo-aware pricing: INR for guests browsing from India (admin FX table),
   USD / EUR / GBP / AED for everyone else based on browsing country. */

export const EUR_COUNTRIES = new Set(["DE", "FR", "ES", "IT", "NL", "IE", "BE", "AT", "PT", "FI", "GR", "LU", "SK", "SI", "EE", "LV", "LT", "CY", "MT", "HR"]);
export const AED_COUNTRIES = new Set(["AE", "SA", "QA", "BH", "KW", "OM"]);
export const CUR_SYMBOL = { USD: "$", EUR: "€", GBP: "£", AED: "AED ", INR: "₹" };

export const currencyForCountry = (cc) => {
    if (!cc) return "USD";
    if (cc === "IN") return "INR";
    if (cc === "GB") return "GBP";
    if (EUR_COUNTRIES.has(cc)) return "EUR";
    if (AED_COUNTRIES.has(cc)) return "AED";
    return "USD";
};

/* Price a tour in any currency. USD/EUR are hand-set per tour; INR uses the
   real list price when known, else admin FX; GBP/AED cross-rate via INR. */
export function tourPriceIn(tour, cur, fx) {
    const rates = fx || { USD: 88.0, GBP: 112.0, AED: 24.0, EUR: 95.0 };
    if (cur === "USD") return tour.priceUsd;
    if (cur === "EUR") return tour.priceEur;
    if (cur === "INR") return tour.priceInr || Math.floor((tour.priceUsd * (rates.USD || 88.0)) / 1000) * 1000 + 999;
    const inr = tour.priceInr || tour.priceUsd * (rates.USD || 88.0);
    const unit = cur === "GBP" ? rates.GBP || 112.0 : rates.AED || 24.0;
    return Math.round(inr / unit / 10) * 10;
}

export const fmtPrice = (n, cur) => n.toLocaleString(cur === "INR" ? "en-IN" : "en-US");

const FALLBACK_INR_PER_UNIT = { USD: 89, EUR: 96, GBP: 112, AED: 22.6 };

/* Format an INR amount in the visitor's geo currency (INR keeps Lakh style). */
export function fmtGeoMoney(inr, cur, fx) {
    if (inr == null) return "—";
    if (!cur || cur === "INR") {
        if (inr >= 100000) {
            const l = inr / 100000;
            return `₹${Number.isInteger(l) ? l : l.toFixed(1)} Lakh`;
        }
        return `₹${Number(inr).toLocaleString("en-IN")}`;
    }
    const rate = (fx && fx[cur]) || FALLBACK_INR_PER_UNIT[cur] || FALLBACK_INR_PER_UNIT.USD;
    const v = inr / rate;
    const rounded = v >= 100 ? Math.round(v) : Math.round(v * 100) / 100;
    return `${CUR_SYMBOL[cur]}${rounded.toLocaleString("en-US")}`;
}

let geoPromise = null;
const fetchGeo = () => {
    const cached = sessionStorage.getItem("geo_cc_v2");
    if (cached) return Promise.resolve(cached);
    if (!geoPromise) {
        geoPromise = api.get("/geo/hint")
            .then(({ data }) => {
                const cc = (data?.country_code || "").toUpperCase();
                if (cc) sessionStorage.setItem("geo_cc_v2", cc);
                return cc;
            })
            .catch(() => "");
    }
    return geoPromise;
};

/** {countryCode, currency, fx, isIndia} — geo + admin FX table, cached per tab.
    A manual header override (localStorage cur_override) beats geo detection. */
export function useGeoPricing() {
    const [countryCode, setCountryCode] = useState(sessionStorage.getItem("geo_cc_v2") || "");
    const [fx, setFx] = useState(null);
    const [override, setOverride] = useState(() => localStorage.getItem("cur_override") || "");

    useEffect(() => {
        fetchGeo().then((cc) => cc && setCountryCode(cc));
        api.get("/fx/public").then(({ data }) => setFx(data?.inr_per_unit || null)).catch(() => {});
        const onOverride = () => setOverride(localStorage.getItem("cur_override") || "");
        window.addEventListener("currency-override", onOverride);
        return () => window.removeEventListener("currency-override", onOverride);
    }, []);

    const currency = override || currencyForCountry(countryCode);
    return { countryCode, currency, fx, isIndia: countryCode === "IN", isOverride: Boolean(override) };
}

export function setCurrencyOverride(cur) {
    if (cur) localStorage.setItem("cur_override", cur);
    else localStorage.removeItem("cur_override");
    window.dispatchEvent(new Event("currency-override"));
}
